import { useMemo, useCallback } from "react";
import { buildLargeGroups, getPassengerCounts } from "../../helpers";
import { classifyPassengerServices } from "../../helpers/passengerServiceNormalizer";
import { countSpecialNeedsCategories } from "../../helpers/ssrCategories";
import {
  buildDynamicBenefitFilters,
  buildRewardTierCounts,
} from "../../helpers/rewardTier";

const MEAL_CATEGORIES = ["Adult", "Child", "Special", "Upgraded"];

export const useFilterCardData = ({
  originalPassengerList = [],
  selectedFilter,
}) => {
  const filterOutExtraSeats = useCallback((passengers) => {
    return passengers.filter(
      (p) => p.passengerType !== "B" && p.passengerType !== "E"
    );
  }, []);

  const actualPassengers = useMemo(() => {
    return filterOutExtraSeats(originalPassengerList);
  }, [filterOutExtraSeats, originalPassengerList]);

  const largeGroups = useMemo(
    () => buildLargeGroups(actualPassengers),
    [actualPassengers]
  );

  const baseFilteredPassengers = useMemo(() => {
    const CHECKED_IN = "CHECKED_IN";
    let result = [...actualPassengers];
    if (selectedFilter?.statusFilter === CHECKED_IN) {
      result = result.filter((p) => p.checkedIn);
    }
    return result;
  }, [actualPassengers, selectedFilter?.statusFilter]);

  const connectionFilteredPassengers = useMemo(() => {
    if (
      selectedFilter?.inboundConnections ||
      selectedFilter?.outboundConnections
    ) {
      return baseFilteredPassengers.filter((p) => {
        const hasInbound = p.inboundConnections?.length > 0;
        const hasOutbound = p.outboundConnections?.length > 0;
        if (selectedFilter.inboundConnections && selectedFilter.outboundConnections) {
          return hasInbound || hasOutbound;
        }
        if (selectedFilter.inboundConnections) return hasInbound;
        if (selectedFilter.outboundConnections) return hasOutbound;
        return false;
      });
    }
    return baseFilteredPassengers;
  }, [
    baseFilteredPassengers,
    selectedFilter?.inboundConnections,
    selectedFilter?.outboundConnections,
  ]);

  const finalFilteredPassengers = useMemo(() => {
    const selectedMeals = selectedFilter?.selectedMeals || [];
    const filterByCategory =
      selectedMeals.length > 0 && !selectedMeals.includes("All");
    if (!filterByCategory) return connectionFilteredPassengers;

    return connectionFilteredPassengers.filter((p) => {
      const category = p?.mealObject?.category;
      return p.mealObject && category && selectedMeals.includes(category);
    });
  }, [connectionFilteredPassengers, selectedFilter?.selectedMeals]);

  const mealCategoryCounts = useMemo(() => {
    return connectionFilteredPassengers.reduce(
      (acc, p) => {
        const category = p?.mealObject?.category;
        if (category && acc[category] !== undefined) acc[category] += 1;
        return acc;
      },
      { Adult: 0, Child: 0, Special: 0, Upgraded: 0 }
    );
  }, [connectionFilteredPassengers]);

  const mealCodeCounts = useMemo(() => {
    const selectedMeals = selectedFilter?.selectedMeals || [];
    const isSpecialSelected = selectedMeals.includes("Special");
    const isAllSelected = selectedMeals.includes("All");

    if ((!isSpecialSelected && !isAllSelected) || selectedMeals.length === 0) {
      return {};
    }

    return finalFilteredPassengers.reduce((acc, p) => {
      const meal = p?.mealObject;
      if (meal?.code && meal?.category === "Special") {
        acc[meal.code] = (acc[meal.code] || 0) + 1;
      }
      return acc;
    }, {});
  }, [finalFilteredPassengers, selectedFilter?.selectedMeals]);

  const {
    countAdultsCheckedIn,
    countInfantsCheckedIn,
    countInfants,
    countSpecialNeeds,
  } = getPassengerCounts(finalFilteredPassengers);

  const specialNeedsCounts = useMemo(
    () => countSpecialNeedsCategories(baseFilteredPassengers),
    [baseFilteredPassengers]
  );

  const derivedCounts = useMemo(() => {
    const counts = {
      specialNeedsCount: specialNeedsCounts.parent,
      idStaffCount: 0,
      otherInfoCount: 0,
      inboundCount: 0,
      outboundCount: 0,
    };

    baseFilteredPassengers.forEach((p) => {
      const classification = classifyPassengerServices(p);
      if (classification.hasIdStaff) counts.idStaffCount += 1;
      if (classification.hasOtherInfo) counts.otherInfoCount += 1;
      if (p.inboundConnections?.length > 0) counts.inboundCount += 1;
      if (p.outboundConnections?.length > 0) counts.outboundCount += 1;
    });

    return counts;
  }, [baseFilteredPassengers, specialNeedsCounts.parent]);

  const rewardTierCounts = useMemo(
    () => buildRewardTierCounts(baseFilteredPassengers),
    [baseFilteredPassengers]
  );

  const dynamicBenefitFilters = useMemo(
    () => buildDynamicBenefitFilters(baseFilteredPassengers),
    [baseFilteredPassengers]
  );

  const totalMeals = connectionFilteredPassengers.filter((p) => p.mealObject)
    .length;
  const hasMeals = Object.values(mealCategoryCounts ?? {}).some(Boolean);

  return {
    actualPassengers,
    baseFilteredPassengers,
    connectionFilteredPassengers,
    finalFilteredPassengers,
    mealCategoryCounts,
    mealCodeCounts,
    hasMeals,
    totalMeals,
    largeGroups,
    rewardTierCounts,
    dynamicBenefitFilters,
    specialNeedsCategoryCounts: specialNeedsCounts.byKey,
    counts: {
      ...derivedCounts,
      countAdultsCheckedIn,
      countInfantsCheckedIn,
      countInfants,
      countSpecialNeeds,
    },
  };
};

export { MEAL_CATEGORIES };
